/**
 * O laço que faz a tela reagir — o que junta `rule-evaluator` e `ScreenFormService`.
 *
 * Sem ele as duas peças existem e nada acontece: as regras sabem responder
 * "verdadeiro ou falso", o formulário sabe escrever, e ninguém liga uma coisa na
 * outra. É este arquivo.
 *
 * O que muda em relação ao módulo antigo:
 *
 * - **Os eventos rodam.** Hoje só `onInit` roda, e só quando `viewMode === 'view'`
 *   — no editor o usuário configura comportamento no escuro.
 * - **`setValue` e `clearValue` escrevem de verdade**, no lugar de `console.log`.
 * - **A condição é reavaliada quando um campo que ela LÊ muda**, e só então.
 *   `collectRulePaths` dá a lista; sem ela, a alternativa seria reavaliar toda
 *   regra da tela a cada tecla digitada.
 * - **O E implícito virou E/OU explícito.** O runtime antigo usava `every()`, ou
 *   seja, todas as condições precisavam valer, e não havia como dizer "ou".
 *
 * A guarda que mais importa: **profundidade de cascata**. Uma ação escreve com
 * `emitEvent: false`, então o Angular não dispara nada; quem decide reagir é este
 * serviço, contando a profundidade. Se A muda B e B muda A, a cadeia para em
 * `MAX_CASCADE_DEPTH` e vira um problema reportado, em vez de travar a aba.
 */
import { DestroyRef, Injectable, Injector, Signal, inject, signal } from '@angular/core';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Action, EventBinding, EventType, RuleExpr } from '../model/behavior';
import { ScreenNode } from '../model/node-props';
import { ScreenDocument } from '../model/screen-schema';
import { RuleIssue, collectRulePaths, evaluateRule } from './rule-evaluator';
import { Router } from '@angular/router';

import { MAX_CASCADE_DEPTH, ScreenForm, ScreenFormService } from './screen-form.service';
import { FormulaServices } from '../../services/formula.service';
import { Variable } from '../../forms/formula-tester/formula-tester.component';
import { DialogService } from '../../services/dialog.service';
import { UserAuthenticationServices } from '../../services/user-authentication.services';
import { FlowModalComponent } from '../../forms/flow-action/flowaction.component';
import {
  DEFAULT_NODE_STATE,
  NodeUiState,
  RuntimeIssue,
  ScreenRuntime
} from './screen-runtime';

/** Espera padrão do `onChange`, quando o evento não declara a sua. */
const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Ações que este runtime executa hoje.
 *
 * As demais (`callFlow`, `navigate`, `recalculate`, `setOptions`) são declaráveis
 * no modelo e chegam nas fases seguintes. Executar metade e ignorar a outra em
 * silêncio seria o pior dos mundos — por isso a ação não suportada vira um
 * `RuntimeIssue` visível, e não um `console.warn` que ninguém lê.
 */
const SUPPORTED_ACTIONS: readonly string[] = [
  'show', 'hide',
  'enable', 'disable',
  'showTab', 'hideTab', 'enableTab', 'disableTab',
  'setValue', 'clearValue',
  'setRequired', 'setReadonly',
  'showMessage',
  'setOptions',
  'navigate',
  'recalculate',
  'callFlow'
];

/**
 * Acoes que agem sobre a TELA, e nao sobre um componente.
 *
 * `navigate` leva para outro lugar e `callFlow` dispara um processo — nenhuma
 * das duas tem alvo, e exigir um faria o usuario inventar um id para satisfazer
 * uma validacao.
 */
const TARGETLESS_ACTIONS: readonly string[] = ['navigate', 'callFlow'];

/** Quantas execucoes de fluxo o painel guarda. */
const MAX_EXECUTIONS = 20;

/** A configuracao de fluxo que o botao carrega. */
interface FlowProps {
  readonly configId?: number;
  readonly configName?: string;
  readonly showCompanySelector?: boolean;
  readonly companyCode?: number;
  readonly newEngine?: boolean;
}

/** O que o botao quer registrar de cada execucao. */
interface FlowLogProps {
  readonly enabled?: boolean;
  readonly capturePayload?: boolean;
  readonly captureResponse?: boolean;
}

/** Uma execucao de fluxo, para o painel de "o que a tela fez". */
export interface ActionExecution {
  readonly id: string;
  readonly buttonId: string;
  readonly startedAt: string;
  readonly durationMs?: number;
  readonly status: 'running' | 'success' | 'error' | 'cancelled';
  readonly flowConfigId?: number;
  readonly flowConfigName?: string;
  /** So quando `capturePayload` esta ligado — ver o comentario em `callFlow`. */
  readonly payload?: unknown;
  readonly errorMessage?: string;
}

/**
 * Le as opcoes de `setOptions` a partir do texto configurado.
 *
 * Formato: `valor|rotulo` separado por `;`. Sem a barra, o valor e o proprio
 * rotulo — que e o caso comum de uma lista de situacoes ja legiveis.
 */
export function parseOptionsForTest(
  raw: string
): readonly { value: string; labelKey: string }[] {
  return parseOptions(raw);
}

function parseOptions(raw: string): readonly { value: string; labelKey: string }[] {
  const opcoes: { value: string; labelKey: string }[] = [];

  for (const parte of raw.split(';')) {
    const texto = parte.trim();

    if (texto.length === 0) {
      continue;
    }

    const corte = texto.indexOf('|');

    if (corte < 0) {
      opcoes.push({ value: texto, labelKey: texto });
      continue;
    }

    opcoes.push({
      value: texto.slice(0, corte).trim(),
      labelKey: texto.slice(corte + 1).trim()
    });
  }

  return opcoes;
}

/** Uma mensagem que a tela pediu para mostrar. Quem exibe é o host. */
/**
 * Um passo do que a tela fez.
 *
 * Existe para uma pergunta só, e é sempre a mesma: *"por que este campo
 * sumiu?"*. Sem a ordem, o painel mostraria um monte de fatos verdadeiros sem
 * relação entre si.
 */
export interface TraceStep {
  /** `event` disparou · `rule` foi avaliada · `action` foi aplicada. */
  readonly kind: 'event' | 'rule' | 'action' | 'skipped';
  /** O nó de onde o passo partiu. */
  readonly nodeId: string;
  /** Gatilho, tipo de regra, ou tipo de ação. */
  readonly what: string;
  /** O alvo, nas ações. */
  readonly targetId?: string;
  /** O resultado, nas regras e nas condições. */
  readonly result?: boolean;
}

/**
 * Quantos passos o rastro guarda.
 *
 * Cem cobre qualquer interação que alguém queira entender. Uma tela com laço de
 * eventos produziria milhares, e é justamente nela que o rastro precisa
 * continuar legível.
 */
const MAX_TRACE_STEPS = 100;

export interface RuntimeMessage {
  readonly messageKey: string;
  readonly nodeId: string;
}

@Injectable()
export class EventRuntimeService implements ScreenRuntime {

  private readonly forms = inject(ScreenFormService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * O roteador e o usuario sao baratos e vem por injecao direta.
   *
   * Opcionais porque uma tela pode ser desenhada fora da aplicacao — num teste,
   * numa previa isolada. Sem eles, a acao vira problema VISIVEL, que e o padrao
   * deste runtime: nunca fingir que executou.
   */
  private readonly router = inject(Router, { optional: true });
  private readonly uas = inject(UserAuthenticationServices, { optional: true });

  /**
   * O dialogo e o motor de formulas sao resolvidos SOB DEMANDA.
   *
   * Injeta-los aqui fecha um ciclo — `EventRuntimeService -> DialogService` —
   * e o `FormulaServices` injeta o dialogo tambem, entao os dois cairiam no
   * mesmo `NG0200`. Resolver na hora da acao conserta o ciclo e e mais honesto:
   * uma tela sem botao de fluxo e sem formula nao tem por que instanciar
   * nenhum dos dois. O runtime nasce uma vez por tela; o dialogo, por clique.
   */
  private readonly injector = inject(Injector);

  private get formulas(): FormulaServices | null {
    return this.injector.get(FormulaServices, null);
  }

  private get dialogs(): DialogService | null {
    return this.injector.get(DialogService, null);
  }

  private readonly _states = signal<ReadonlyMap<string, NodeUiState>>(new Map());
  private readonly _issues = signal<readonly RuntimeIssue[]>([]);

  /**
   * O historico de execucoes de fluxo desta tela.
   *
   * Em memoria e limitado: e o painel "o que a tela fez", nao um log de
   * auditoria. Auditoria de verdade grava no banco, e isso e `log.persist`,
   * que ainda nao existe.
   */
  private readonly _executions = signal<readonly ActionExecution[]>([]);
  private readonly _messages = signal<readonly RuntimeMessage[]>([]);

  /**
   * O rastro, e o interruptor dele.
   *
   * Desligado por padrão: em produção ninguém liga, e o custo passa a ser uma
   * comparação booleana por evento. Só o "Simular" do construtor liga.
   */
  private readonly _trace = signal<readonly TraceStep[]>([]);
  private tracing = false;

  readonly states: Signal<ReadonlyMap<string, NodeUiState>> = this._states.asReadonly();
  readonly issues: Signal<readonly RuntimeIssue[]> = this._issues.asReadonly();

  readonly executions: Signal<readonly ActionExecution[]> = this._executions.asReadonly();
  readonly messages: Signal<readonly RuntimeMessage[]> = this._messages.asReadonly();

  /** O que a tela fez, na ordem. Vazio enquanto o rastro estiver desligado. */
  readonly trace: Signal<readonly TraceStep[]> = this._trace.asReadonly();

  /** Liga ou desliga o rastro. Desligar zera o que havia. */
  setTracing(on: boolean): void {
    this.tracing = on;

    if (!on) {
      this._trace.set([]);
    }
  }

  clearTrace(): void {
    this._trace.set([]);
  }

  /**
   * Registra um passo — só quando o rastro está ligado.
   *
   * O limite não é economia: uma tela com laço de eventos encheria a memória
   * antes de alguém perceber, e o rastro serve para entender o que ACABOU de
   * acontecer, não para guardar tudo.
   */
  private step(step: TraceStep): void {
    if (!this.tracing) {
      return;
    }

    this._trace.update(list => {
      const proximo = [...list, step];
      return proximo.length > MAX_TRACE_STEPS
        ? proximo.slice(proximo.length - MAX_TRACE_STEPS)
        : proximo;
    });
  }

  private document: ScreenDocument | null = null;
  private screenForm: ScreenForm | null = null;

  /** Nós indexados por id, para achar alvo de ação sem varrer a árvore. */
  private nodeById = new Map<string, ScreenNode>();

  /**
   * Sobrescritas imperativas: o que uma AÇÃO determinou, e que vence a regra.
   *
   * Existe porque as duas coisas convivem. `visibleWhen` é contínuo — vale
   * enquanto a condição valer. Já `hide` é pontual: aconteceu um evento e o
   * campo sumiu. Sem separar, a próxima reavaliação das regras desfaria a ação,
   * e o usuário veria o campo voltar sozinho.
   */
  private readonly overrides = new Map<string, Partial<NodeUiState>>();

  private readonly subscriptions: Subscription[] = [];
  private readonly changeTriggers = new Map<string, Subject<void>>();

  // ---------------------------------------------------------------- início

  /**
   * Liga o runtime a uma tela.
   *
   * Chamar de novo com outro documento desliga o anterior — é o caso do
   * `simple-datagrid`, que troca de tela sem destruir o componente.
   */
  start(document: ScreenDocument, screenForm: ScreenForm): void {
    this.stop();

    this.document = document;
    this.screenForm = screenForm;
    this.nodeById = this.indexNodes(document);

    this.evaluateAllRules(0);
    this.watchRuleInputs();
    this.watchChangeEvents();
    this.runInitEvents();
  }

  stop(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions.length = 0;

    for (const trigger of this.changeTriggers.values()) {
      trigger.complete();
    }
    this.changeTriggers.clear();

    this.overrides.clear();
    this._states.set(new Map());
  }

  stateOf(nodeId: string): NodeUiState {
    return this._states().get(nodeId) ?? DEFAULT_NODE_STATE;
  }

  /** Limpa as mensagens já mostradas. Quem exibe chama depois de exibir. */
  consumeMessages(): readonly RuntimeMessage[] {
    const pending = this._messages();
    this._messages.set([]);
    return pending;
  }

  // ---------------------------------------------------------------- regras

  /**
   * Reavalia `visibleWhen`, `disabledWhen` e `requiredWhen` de todos os nós.
   *
   * Roda inteiro no início e depois só quando um campo lido por ALGUMA regra
   * muda — o que é raro comparado a "toda tecla". Reavaliar a tela toda nesse
   * momento é mais simples e mais seguro que reavaliar só as regras afetadas:
   * regras encadeadas (A depende de B, que depende de C) ficariam
   * dessincronizadas com a otimização, e o custo aqui é uma varredura de
   * dezenas de nós, não milhares.
   */
  private evaluateAllRules(depth: number): void {
    const document = this.document;
    const screenForm = this.screenForm;

    if (document === null || screenForm === null) {
      return;
    }

    if (depth > MAX_CASCADE_DEPTH) {
      this.reportIssue('cascadeTooDeep', document.root.id,
        'reavaliação em cadeia passou de ' + MAX_CASCADE_DEPTH + ' níveis');
      return;
    }

    const resolve = this.forms.createResolver(screenForm);
    const next = new Map<string, NodeUiState>();

    for (const node of this.nodeById.values()) {
      const behavior = node.behavior;

      let visible = true;
      let disabled = false;
      let required = this.isStaticallyRequired(node);

      if (behavior !== undefined) {
        visible = this.evaluate(behavior.visibleWhen, resolve, node.id, true);
        disabled = this.evaluate(behavior.disabledWhen, resolve, node.id, false);

        if (behavior.requiredWhen !== undefined) {
          required = this.evaluate(behavior.requiredWhen, resolve, node.id, false);
        }
      }

      const override = this.overrides.get(node.id);
      if (override !== undefined) {
        visible = override.visible ?? visible;
        disabled = override.disabled ?? disabled;
        required = override.required ?? required;
      }

      next.set(node.id, { visible, disabled, required });
    }

    this._states.set(next);
    this.applyStatesToForm(next);
  }

  /**
   * `visibleWhen` ausente devolve o padrão; presente e não avaliável devolve
   * `false` — o avaliador falha fechado, e aqui isso é preservado.
   */
  private evaluate(
    rule: RuleExpr | undefined,
    resolve: (path: string) => { found: boolean; value: unknown },
    nodeId: string,
    whenAbsent: boolean
  ): boolean {

    if (rule === undefined) {
      return whenAbsent;
    }

    return evaluateRule(rule, {
      resolve,
      onIssue: (issue: RuleIssue) => {
        this.reportIssue(this.mapIssueKind(issue.kind), nodeId, issue.detail);
      }
    });
  }

  private mapIssueKind(kind: RuleIssue['kind']): RuntimeIssue['kind'] {
    if (kind === 'unknownPath') {
      return 'unknownPath';
    }
    return 'badRule';
  }

  private isStaticallyRequired(node: ScreenNode): boolean {
    const props = node.props as { required?: boolean };
    return props.required === true;
  }

  /**
   * Reflete o estado calculado no formulário.
   *
   * Campo escondido ou desabilitado por regra precisa parar de ser validado —
   * senão a tela fica inválida por causa de um campo obrigatório que o usuário
   * nem consegue ver, e ninguém entende por que o save não passa.
   */
  private applyStatesToForm(states: ReadonlyMap<string, NodeUiState>): void {
    const screenForm = this.screenForm;
    if (screenForm === null) {
      return;
    }

    for (const [nodeId, state] of states) {
      if (!screenForm.controlByNodeId.has(nodeId)) {
        continue;
      }

      const effectivelyRequired = state.required && state.visible && !state.disabled;
      this.forms.setRequired(screenForm, nodeId, effectivelyRequired);
      this.forms.setReadonly(screenForm, nodeId, state.disabled);
    }
  }

  /**
   * Observa só os campos que alguma regra LÊ.
   *
   * A lista sai de `collectRulePaths`. Um caminho pode casar com mais de um
   * controle (dois componentes lendo o mesmo `dataPath` é comum), então todos
   * entram.
   */
  private watchRuleInputs(): void {
    const screenForm = this.screenForm;
    if (screenForm === null) {
      return;
    }

    const watched = new Set<string>();

    for (const node of this.nodeById.values()) {
      const behavior = node.behavior;
      if (behavior === undefined) {
        continue;
      }

      const rules = [behavior.visibleWhen, behavior.disabledWhen, behavior.requiredWhen];
      for (const rule of rules) {
        for (const path of collectRulePaths(rule)) {
          const controls = screenForm.controlsByPath.get(path);
          if (controls === undefined) {
            continue;
          }
          for (const controlName of controls) {
            watched.add(controlName);
          }
        }
      }
    }

    for (const controlName of watched) {
      const control = screenForm.group.get(controlName);
      if (control === null) {
        continue;
      }

      const subscription = control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.evaluateAllRules(0);
        });

      this.subscriptions.push(subscription);
    }
  }

  // ---------------------------------------------------------------- eventos

  private runInitEvents(): void {
    for (const node of this.nodeById.values()) {
      this.fire(node, 'onInit', 0);
    }
  }

  /**
   * Liga o `onChange` de cada campo que declara um.
   *
   * O debounce é por campo, não global: um `onChange` que consulta o banco não
   * pode disparar a cada tecla, e um que só esconde um campo não deveria esperar.
   */
  private watchChangeEvents(): void {
    const screenForm = this.screenForm;
    if (screenForm === null) {
      return;
    }

    for (const node of this.nodeById.values()) {
      const bindings = this.bindingsFor(node, 'onChange');
      if (bindings.length === 0) {
        continue;
      }

      const controlName = screenForm.controlByNodeId.get(node.id);
      if (controlName === undefined) {
        continue;
      }

      const control = screenForm.group.get(controlName);
      if (control === null) {
        continue;
      }

      const trigger = new Subject<void>();
      this.changeTriggers.set(node.id, trigger);

      const wait = bindings[0].debounceMs ?? DEFAULT_DEBOUNCE_MS;

      const debounced = trigger
        .pipe(debounceTime(wait), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.fire(node, 'onChange', 0);
        });

      const source = control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          trigger.next();
        });

      this.subscriptions.push(debounced, source);
    }
  }

  /**
   * Dispara um evento de um nó. Público: o botão chama com `onClick`, a grade
   * com `onRowSelect`.
   */
  trigger(nodeId: string, event: EventType): void {
    const node = this.nodeById.get(nodeId);

    if (node === undefined) {
      this.reportIssue('unknownTarget', nodeId, 'nó não existe na tela');
      return;
    }

    this.fire(node, event, 0);
  }

  private fire(node: ScreenNode, event: EventType, depth: number): void {
    if (depth > MAX_CASCADE_DEPTH) {
      this.reportIssue('cascadeTooDeep', node.id,
        'cadeia de eventos passou de ' + MAX_CASCADE_DEPTH + ' níveis');
      return;
    }

    const screenForm = this.screenForm;
    if (screenForm === null) {
      return;
    }

    const resolve = this.forms.createResolver(screenForm);

    for (const binding of this.bindingsFor(node, event)) {
      if (binding.disabled === true) {
        continue;
      }

      this.step({ kind: 'event', nodeId: node.id, what: event });

      const shouldRun = this.evaluate(binding.when, resolve, node.id, true);

      if (!shouldRun) {
        // A condição FALSA é o passo mais informativo do rastro: é a resposta
        // para "configurei a regra e nada aconteceu".
        this.step({ kind: 'skipped', nodeId: node.id, what: event, result: false });
        continue;
      }

      if (binding.when !== undefined) {
        this.step({ kind: 'rule', nodeId: node.id, what: 'when', result: true });
      }

      for (const action of binding.actions) {
        this.step({
          kind: 'action',
          nodeId: node.id,
          what: action.type,
          targetId: action.target
        });

        this.execute(action, node, depth);
      }
    }

    // As ações escrevem com `emitEvent: false`, então nada reavaliaria as regras
    // sozinho. Reavaliar aqui, uma vez por evento, é o que mantém a tela
    // coerente sem abrir caminho para laço.
    this.evaluateAllRules(depth + 1);
  }

  private bindingsFor(node: ScreenNode, event: EventType): readonly EventBinding[] {
    const events = node.behavior?.events;
    if (events === undefined) {
      return [];
    }

    const matching: EventBinding[] = [];
    for (const binding of events) {
      if (binding.event === event) {
        matching.push(binding);
      }
    }
    return matching;
  }

  // ---------------------------------------------------------------- ações

  private execute(action: Action, source: ScreenNode, depth: number): void {
    const screenForm = this.screenForm;
    if (screenForm === null) {
      return;
    }

    if (!SUPPORTED_ACTIONS.includes(action.type)) {
      this.reportIssue('actionNotSupported', source.id,
        'a ação "' + action.type + '" ainda não é executada por este motor');
      return;
    }

    if (action.type === 'showMessage') {
      if (action.messageKey !== undefined) {
        this._messages.update(list => [...list, {
          messageKey: action.messageKey as string,
          nodeId: source.id
        }]);
      }
      return;
    }

    if (action.type === 'navigate') {
      this.navigateTo(action, source);
      return;
    }

    if (action.type === 'callFlow') {
      this.callFlow(action, source);
      return;
    }

    const targetId = action.target;
    if (targetId === undefined || targetId.length === 0) {
      this.reportIssue('unknownTarget', source.id,
        'a ação "' + action.type + '" não diz sobre qual componente age');
      return;
    }

    if (!this.nodeById.has(targetId)) {
      // Referência órfã: o alvo foi removido no editor e o comportamento ficou.
      // Hoje isso falha em silêncio — é um dos erros que o "Verificar tela" lista.
      this.reportIssue('unknownTarget', source.id,
        'a ação "' + action.type + '" aponta para "' + targetId + '", que não existe');
      return;
    }

    this.applyToTarget(action, targetId, screenForm);
  }

  private applyToTarget(action: Action, targetId: string, screenForm: ScreenForm): void {
    if (action.type === 'show' || action.type === 'showTab') {
      this.setOverride(targetId, { visible: true });
      return;
    }
    if (action.type === 'hide' || action.type === 'hideTab') {
      this.setOverride(targetId, { visible: false });
      return;
    }
    if (action.type === 'enable' || action.type === 'enableTab') {
      this.setOverride(targetId, { disabled: false });
      return;
    }
    if (action.type === 'disable' || action.type === 'disableTab') {
      this.setOverride(targetId, { disabled: true });
      return;
    }
    if (action.type === 'setRequired') {
      this.setOverride(targetId, { required: action.value !== false });
      return;
    }
    if (action.type === 'setReadonly') {
      this.setOverride(targetId, { disabled: action.value !== false });
      return;
    }
    if (action.type === 'setValue') {
      this.forms.writeValue(screenForm, targetId, action.value ?? null);
      return;
    }
    if (action.type === 'clearValue') {
      this.forms.clearValue(screenForm, targetId);
      return;
    }
    if (action.type === 'setOptions') {
      this.setOptions(action, targetId);
      return;
    }
    if (action.type === 'recalculate') {
      this.recalculate(action, targetId, screenForm);
      return;
    }
  }

  /**
   * Troca as opcoes de uma lista em tempo de execucao.
   *
   * O valor vem como texto — `"aberto|Aberto;fechado|Fechado"` ou um caminho no
   * dado da tela. Duas formas porque os dois casos existem: lista fixa que muda
   * por regra, e lista que vem do que foi carregado.
   *
   * As opcoes vao para o ESTADO do no, nao para as props: props sao o documento,
   * e o documento e a configuracao da tela — mudar props em tempo de execucao
   * faria a tela "se editar sozinha" e a proxima gravacao levaria junto.
   */
  private setOptions(action: Action, targetId: string): void {
    const raw = action.value;

    if (typeof raw !== 'string' || raw.trim().length === 0) {
      this.setOverride(targetId, { options: [] });
      return;
    }

    this.setOverride(targetId, { options: parseOptions(raw) });
  }

  /**
   * Recalcula o valor de um campo por FORMULA.
   *
   * Nao interpreta codigo: manda a expressao para o `FormulaServices`, que e o
   * mesmo motor que o resto do produto usa e que avalia no servidor. E o que a
   * secao 8.3 do plano pedia ao deletar o `new Function` — trocar de mecanismo,
   * nao de nome.
   *
   * Assincrono, e por isso o valor chega depois. Um `recalculate` que dependesse
   * de outro `recalculate` na mesma acao veria o valor antigo; isso e limitacao
   * conhecida e esta anotada, em vez de fingir sincronismo.
   */
  private recalculate(action: Action, targetId: string, screenForm: ScreenForm): void {
    const expression = action.value;

    if (typeof expression !== 'string' || expression.trim().length === 0) {
      this.reportIssue('actionNotSupported', targetId,
        'a acao "recalculate" nao tem formula para calcular');
      return;
    }

    if (this.formulas === null) {
      this.reportIssue('actionNotSupported', targetId,
        'o motor de formulas nao esta disponivel nesta tela');
      return;
    }

    const variables = this.formulaVariables(screenForm);

    this.formulas.executeFormula(expression, variables)
      .then((resultado: unknown) => {
        this.forms.writeValue(screenForm, targetId, resultado ?? null);
      })
      .catch(() => {
        this.reportIssue('actionNotSupported', targetId,
          'a formula de "recalculate" nao pode ser calculada');
      });
  }

  /**
   * Os valores da tela, no formato que o motor de formulas espera.
   *
   * `key`, `value` e `type` sao os nomes que o `FormulaServices` le — os mesmos
   * que o testador de formulas do projeto usa. O valor vai como TEXTO porque e
   * assim que a expressao e montada do outro lado.
   */
  private formulaVariables(screenForm: ScreenForm): Variable[] {
    const documento = this.document;

    if (documento === null) {
      return [];
    }

    const valores = this.forms.collectValues(screenForm, documento);
    const variaveis: Variable[] = [];

    for (const [nome, valor] of Object.entries(valores)) {
      variaveis.push({
        key: nome,
        value: valor === null || valor === undefined ? '' : String(valor),
        type: typeof valor
      });
    }

    return variaveis;
  }

  /**
   * Dispara o fluxo configurado no botao, e REGISTRA o que aconteceu.
   *
   * O registro e metade da funcionalidade, nao enfeite. Ate aqui o usuario
   * clicava em "executar", recebia uma confirmacao generica e nao tinha como
   * saber se o fluxo rodou, o que foi enviado, o que voltou nem por que falhou —
   * o `console.log` do desenvolvedor nao serve para quem opera a tela.
   *
   * O payload NAO e guardado por padrao: o de uma tela de pedido carrega dado de
   * cliente, e grava-lo sem ninguem pedir seria criar um registro de dado pessoal
   * por conta propria. Quem quiser liga em `props.log.capturePayload`.
   */
  private callFlow(action: Action, source: ScreenNode): void {
    const props = source.props as { flow?: FlowProps; log?: FlowLogProps };
    const flow = props.flow;

    if (flow === undefined || flow.configId === undefined) {
      this.reportIssue('actionNotSupported', source.id,
        'o botao nao tem fluxo escolhido');
      return;
    }

    if (this.dialogs === null || this.screenForm === null) {
      this.reportIssue('actionNotSupported', source.id,
        'esta tela nao consegue abrir o fluxo');
      return;
    }

    const documento = this.document;

    if (documento === null) {
      this.reportIssue('actionNotSupported', source.id,
        'esta tela ainda nao terminou de carregar');
      return;
    }

    const empresa = flow.companyCode ?? Number(this.uas?.user.codecompany ?? 0);
    const valores = this.forms.collectValues(this.screenForm, documento);

    const registro: ActionExecution = {
      id: source.id + ':' + this._executions().length,
      buttonId: source.id,
      startedAt: new Date().toISOString(),
      status: 'running',
      flowConfigId: flow.configId,
      flowConfigName: flow.configName,
      payload: props.log?.capturePayload === true ? valores : undefined
    };

    this.pushExecution(registro);

    this.dialogs.openGenericDialog(
      FlowModalComponent,
      {
        action: {
          NAME: flow.configName ?? 'Fluxo',
          ICON: 'rocket',
          IDCONF: String(flow.configId),
          CODECOMPANY: String(empresa),
          // `NM` e o motor novo (assincrono); `C`, o antigo. E a mesma
          // distincao que o dialogo de configuracao grava.
          TYPE: flow.newEngine === true ? 'NM' : 'C',
          EXPRESSION: JSON.stringify({
            idConf: flow.configId,
            codeCompany: empresa,
            showCodeCompany: flow.showCompanySelector
          }),
          SELECTED: null,
          IDINSTANCE: null,
          IDACTION: null
        },
        selected: [valores],
        parameters: {},
        function: null
      },
      () => this.finishExecution(registro.id, 'success'),
      () => this.finishExecution(registro.id, 'cancelled')
    );
  }

  /** Guarda a execucao, mantendo so as ultimas. */
  private pushExecution(entry: ActionExecution): void {
    const atual = this._executions();
    const proximo = [...atual, entry];

    this._executions.set(
      proximo.length > MAX_EXECUTIONS
        ? proximo.slice(proximo.length - MAX_EXECUTIONS)
        : proximo
    );
  }

  private finishExecution(id: string, status: ActionExecution['status']): void {
    this._executions.set(this._executions().map(entry => {
      if (entry.id !== id) {
        return entry;
      }

      const inicio = new Date(entry.startedAt).getTime();

      return {
        ...entry,
        status,
        durationMs: Number.isFinite(inicio) ? Date.now() - inicio : undefined
      };
    }));
  }

  /**
   * Leva para outra tela.
   *
   * `value` e a rota. Rota externa (comecando com http) abre em outra aba: uma
   * tela de negocio que navega para fora e perde o trabalho em aberto seria
   * surpresa, e nao funcionalidade.
   */
  private navigateTo(action: Action, source: ScreenNode): void {
    const destino = action.value;

    if (typeof destino !== 'string' || destino.trim().length === 0) {
      this.reportIssue('unknownTarget', source.id,
        'a acao "navigate" nao diz para onde ir');
      return;
    }

    if (destino.startsWith('http://') || destino.startsWith('https://')) {
      window.open(destino, '_blank', 'noopener');
      return;
    }

    if (this.router === null) {
      this.reportIssue('actionNotSupported', source.id,
        'esta tela nao tem roteador para navegar');
      return;
    }

    void this.router.navigateByUrl(destino);
  }

  private setOverride(nodeId: string, patch: Partial<NodeUiState>): void {
    const current = this.overrides.get(nodeId);

    if (current === undefined) {
      this.overrides.set(nodeId, { ...patch });
      return;
    }

    this.overrides.set(nodeId, { ...current, ...patch });
  }

  // ---------------------------------------------------------------- apoio

  private indexNodes(document: ScreenDocument): Map<string, ScreenNode> {
    const index = new Map<string, ScreenNode>();

    const visit = (node: ScreenNode): void => {
      index.set(node.id, node);
      if (node.children !== undefined) {
        for (const child of node.children) {
          visit(child);
        }
      }
    };

    visit(document.root);
    return index;
  }

  private reportIssue(kind: RuntimeIssue['kind'], nodeId: string, detail: string): void {
    const already = this._issues();

    // Sem deduplicação, uma regra órfã num campo observado geraria um problema
    // novo a cada tecla digitada, e a lista viraria ruído.
    for (const issue of already) {
      if (issue.kind === kind && issue.nodeId === nodeId && issue.detail === detail) {
        return;
      }
    }

    this._issues.set([...already, { kind, nodeId, detail }]);
  }
}
